/* Product requirements, verified dimensions and real necklace variants. */
(() => {
  'use strict';
  const escape = value => String(value ?? '').replace(/[&<>\'\"]/g, char => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[char]));
  const fieldLabel = (key, category) => ({
    item:category === 'open-cuff' || category === 'open-ring' ? 'Band' : 'Product', pendant:'Pendant', personalizationArea:'Personalization area', chain:'Chain', bracelet:'Bracelet',
    charm:'Charm', keyring:'Key ring', fitRange:'Fit range', innerDiameter:'Inner diameter', openingGap:'Opening gap',
    openLocket:'Open locket', components:'Set components', giftBox:'Gift box', necklace:'Necklace', ring:'Ring',
    length:'Length', width:'Width', height:'Height', thickness:'Thickness', diameter:'Diameter', depth:'Depth', extension:'Extension'
  }[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, char => char.toUpperCase()));
  const flatten = (value, path = [], root = [], category = '') => Object.entries(value || {}).flatMap(([key, item]) => {
    const next = [...path, fieldLabel(key, category)];
    const fieldPath = [...root, key].join('.');
    if (Number.isFinite(item) && item > 0) return [[next.join(' · '), `${item} cm`, fieldPath]];
    return item && typeof item === 'object' ? flatten(item, next, [...root, key], category) : [];
  });
  const approximateNote = 'Approximate dimensions; items are not individually measured. Hand measurement or production may vary; actual item may differ.';
  const dimensionRows = dimensions => ['estimated','verified'].includes(dimensions?.status) && dimensions.unit === 'cm'
    ? flatten(dimensions.valuesCm, [], [], dimensions.category).filter(([, , fieldPath]) => !dimensions.hiddenCustomerPaths?.includes(fieldPath)).map(([label, value]) => `<div><dt>${escape(label)}</dt><dd>${dimensions.status === 'estimated' ? 'Approx. ' : ''}${escape(value)}</dd></div>`).join('')
    : '';
  const dimensionNote = dimensions => dimensions?.status === 'estimated' ? `<p class="ks-dimension-note">${escape(approximateNote)}</p>` : '';
  const realChainLengths = (product, dimensions, registry) => {
    const dataNode = product.querySelector('.r-product-data');
    if (!dataNode || !registry.variantHelpers?.select) return [];
    let variants;
    try { variants = JSON.parse(dataNode.textContent).variants || []; } catch { return []; }
    const finishes = (product.dataset.supportedFinishes || 'gold,silver').split(',');
    return (dimensions?.chainOptionsCm || []).filter(length =>
      finishes.every(finish => registry.variantHelpers.select(variants, finish, length))
    );
  };

  const boot = () => {
    const registry = window.KSPersonalizerRegistry;
    if (!registry) return;
    const product = document.querySelector('[data-r-product]');
    const sku = product?.dataset.sku;
    const spec = registry.specs[sku];
    const dimensions = spec?.dimensions;

    if (product && spec) {
      const personalize = product.querySelector('[data-personalize]');
      if (personalize && !product.querySelector('[data-spec-link]')) {
        const link = document.createElement('a');
        link.className = 'r-spec-link';
        link.dataset.specLink = '';
        link.href = `/pages/personalization-guide#spec-${encodeURIComponent(sku)}`;
        link.textContent = 'See personalization requirements';
        personalize.insertAdjacentElement('afterend', link);
      }

      const lengthControl = product.querySelector('[data-chain-length-control]');
      const select = lengthControl?.querySelector('[data-chain-length]');
      const lengths = realChainLengths(product, dimensions, registry);
      if (select && lengths.length) {
        lengths.forEach(length => select.add(new Option(registry.variantHelpers.chainLengthLabel(length), Number(length).toFixed(1))));
        lengthControl.hidden = false;
      }

      const sizeRows = dimensionRows(dimensions);
      if (sizeRows || dimensions?.fitDisclosure) {
        const accordion = product.querySelector('.r-accordions');
        if (accordion && !accordion.querySelector('[data-size-fit]')) {
          const details = document.createElement('details');
          details.dataset.sizeFit = '';
          details.innerHTML = `<summary>Size &amp; fit</summary>${dimensions.fitDisclosure ? `<p>${escape(dimensions.fitDisclosure)}</p>` : ''}${dimensionNote(dimensions)}${sizeRows ? `<dl>${sizeRows}</dl>` : ''}`;
          accordion.prepend(details);
        }
      }
    }

    const requirementsGuide = document.querySelector('[data-spec-guide]');
    if (requirementsGuide) {
      let salesMode = 'preview';
      try {
        const settings = JSON.parse(document.getElementById('r-settings')?.textContent || '{}');
        salesMode = settings.checkoutEnabled === true ? 'test' : 'preview';
      } catch {}
      requirementsGuide.innerHTML = Object.entries(registry.handleToSku).map(([handle, productSku]) => {
        const productSpec = registry.specs[productSku];
        if (!productSpec) return '';
        const size = productSpec.dimensions;
        const acceptsImage = registry.products[productSku]?.input !== 'text';
        const privacy = salesMode === 'preview'
          ? (acceptsImage ? 'Your original stays in this browser preview. No order or upload is created.' : 'Your text stays in this browser preview. No order is created.')
          : 'Adding this design to the bag uploads its rendered preview and any source images to Shopify. File links and design details stay with the matching product line.';
        const sizeRows = dimensionRows(size);
        const chainLengths = size?.chainOptionsCm || [];
        const productUrl = `/products/${handle}`;
        return `<article class="ks-spec-card" id="spec-${escape(productSku)}" data-spec-card="${escape(productSku)}"><header><p class="ks-eyebrow">${escape(productSku)}</p><h2>${escape(registry.products[productSku]?.label || productSku)}</h2><p class="ks-spec-kind">${escape(productSpec.kind)}</p></header><dl><div><dt>Prepare</dt><dd>${escape(productSpec.preparation)}</dd></div>${acceptsImage ? `<div><dt>Upload</dt><dd>${escape(productSpec.count)}</dd></div><div><dt>File rules</dt><dd>${escape(productSpec.formats)} · under ${escape(productSpec.maxFile)} · below ${escape(productSpec.maxPixels)}</dd></div>` : ''}<div><dt>Avoid</dt><dd>${escape(productSpec.mistakes)}</dd></div>${productSpec.textLimit ? `<div><dt>Text limit</dt><dd>${escape(productSpec.textLimit)}</dd></div>` : ''}${productSpec.finishes ? `<div><dt>Finish</dt><dd>${escape(productSpec.finishes)}</dd></div>` : ''}${chainLengths.length ? `<div data-chain-length-choices><dt>Chain length choices</dt><dd>${chainLengths.map(length => escape(registry.variantHelpers.chainLengthLabel(length))).join(' · ')}</dd></div>` : ''}${sizeRows}</dl>${dimensionNote(size)}${size?.fitDisclosure ? `<p>${escape(size.fitDisclosure)}</p>` : ''}<p class="ks-spec-privacy">${escape(privacy)}</p><a class="ks-text-link" href="${productUrl}">Start this personalization ↗</a></article>`;
      }).join('');
      const requested = decodeURIComponent(location.hash.replace('#spec-', ''));
      if (requested) document.getElementById(`spec-${requested}`)?.scrollIntoView({ block: 'start' });
    }

    const dimensionGuide = document.querySelector('[data-dimension-guide]');
    if (dimensionGuide) {
      dimensionGuide.innerHTML = Object.entries(registry.handleToSku).map(([handle, productSku]) => {
        const size = registry.specs[productSku]?.dimensions;
        const sizeRows = dimensionRows(size);
        if (!sizeRows && !size?.fitDisclosure) return '';
        return `<article class="ks-spec-card" id="dimensions-${escape(productSku)}"><header><p class="ks-eyebrow">${escape(productSku)}</p><h2>${escape(registry.products[productSku]?.label || productSku)}</h2><p class="ks-spec-kind">Size &amp; fit</p></header>${size?.fitDisclosure ? `<p>${escape(size.fitDisclosure)}</p>` : ''}${dimensionNote(size)}${sizeRows ? `<dl>${sizeRows}</dl>` : ''}<a class="ks-text-link" href="/products/${escape(handle)}">Open product ↗</a></article>`;
      }).join('');
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
})();
